import { createMachine } from '@glory/kernel';

import type { SigningMode } from './kinds.ts';
import type { IsoDate } from './model.ts';

/**
 * Пакет на подпись (раздел 4 спецификации подпроекта 4): что подписывается вместе. Заявка со спецификацией и
 * счётом на предоплату, акт клиенту, допсоглашение, договор с сотрудником, акт о выплате.
 */
export const PACKAGE_KINDS = ['client_order', 'act', 'amendment', 'employee_contract', 'payout_act'] as const;
export type PackageKind = (typeof PACKAGE_KINDS)[number];

/**
 * Хранимый статус пакета: черновик собирается, «на подписи» — идёт очередь подписантов, дальше — подписан,
 * отказ стороны или отозван. Истечение ссылки не хранится, а вычисляется из `valid_until` (раздел 5).
 */
export const PACKAGE_STATUSES = ['draft', 'signing', 'signed', 'declined', 'revoked'] as const;
export type PackageStatus = (typeof PACKAGE_STATUSES)[number];

/** Состояние для показа: статус и «истекло» — пакет на подписи, срок ссылки прошёл. */
export const PACKAGE_STATES = [...PACKAGE_STATUSES, 'expired'] as const;
export type PackageState = (typeof PACKAGE_STATES)[number];

/**
 * Ежедневное напоминание (подпроект 4.4): сотруднику — подписать договор, админам — приложить скан бумажного
 * оригинала штатного договора.
 */
export const REMINDER_KINDS = ['employee_contract', 'paper_scan'] as const;
export type ReminderKind = (typeof REMINDER_KINDS)[number];

/** Чем подписан пакет: кодом из письма (ПЭП) или сканом бумажных экземпляров. */
export const SIGNED_VIA = ['code', 'scan'] as const;
export type SignedVia = (typeof SIGNED_VIA)[number];

/** Сторона подписанта: студия (подписант из реквизитов) или другая сторона — клиент, сотрудник. */
export const SIGNER_SIDES = ['studio', 'party'] as const;
export type SignerSide = (typeof SIGNER_SIDES)[number];

export const SIGNER_STATUSES = ['pending', 'signed', 'declined'] as const;
export type SignerStatus = (typeof SIGNER_STATUSES)[number];

/** Способ подписи в листе подписания: код из письма или скан бумажного документа. */
export const SIGNATURE_METHODS = ['email_code', 'scan'] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];

/**
 * Переходы пакета. Заморозка отправляет его на подпись; подпись последнего в очереди или скан завершают; отказ
 * стороны и отзыв — конец пути: дальше собирается новый пакет. Подписанный не меняется никогда.
 */
export const packageLifecycle = createMachine({
  draft: { freeze: 'signing', scan: 'signed' },
  signing: { complete: 'signed', scan: 'signed', decline: 'declined', revoke: 'revoked' },
  signed: {},
  declined: {},
  revoked: {},
});

/** Кто подписывает по порядку: «Кто подписывает» вида документа → стороны очереди. */
export function signerSides(mode: SigningMode): SignerSide[] {
  switch (mode) {
    case 'none':
      return [];
    case 'studio':
      return ['studio'];
    case 'party':
      return ['party'];
    case 'studio_then_party':
      return ['studio', 'party'];
    case 'party_then_studio':
      return ['party', 'studio'];
  }
}

/** Пакет на подписи, срок ссылки прошёл: подписать нельзя, пока ответственный не продлит ссылку. */
export function packageState(
  status: PackageStatus,
  validUntil: IsoDate | null,
  today: IsoDate,
): PackageState {
  if (status === 'signing' && validUntil !== null && validUntil < today) return 'expired';
  return status;
}

/** Подписант в очереди: позиция и статус. */
export interface QueuedSigner {
  readonly id: string;
  readonly position: number;
  readonly status: SignerStatus;
}

/** Чья очередь: первый по позиции, кто ещё не подписал; все подписали (или отказ) — никого. */
export function currentSigner<S extends QueuedSigner>(signers: readonly S[]): S | null {
  const ordered = [...signers].sort((a, b) => a.position - b.position);
  for (const signer of ordered) {
    if (signer.status === 'declined') return null;
    if (signer.status === 'pending') return signer;
  }
  return null;
}

/** Подпись этого подписанта завершает пакет: после него в очереди никого не осталось. */
export function isLastSigner(signers: readonly QueuedSigner[], signerId: string): boolean {
  return signers.every((signer) => signer.id === signerId || signer.status === 'signed');
}

/** Что ответственный может сделать с пакетом в этом состоянии. */
export const canRevokePackage = (state: PackageState): boolean => state === 'signing' || state === 'expired';
export const canExtendPackage = (state: PackageState): boolean => state === 'signing' || state === 'expired';
export const canUploadPackageScan = (state: PackageState): boolean =>
  state === 'draft' || state === 'signing' || state === 'expired';

/** Срок ссылки: сегодня + `link_valid_days` по календарю (ссылка действует весь последний день). */
export function linkValidUntil(today: IsoDate, days: number): IsoDate {
  const date = new Date(`${today}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Почта для показа на странице подписания: первая буква, звёздочки и домен — «i***@example.ru». */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0) return email;
  const local = email.slice(0, at);
  return `${local.slice(0, 1)}${'*'.repeat(Math.min(Math.max(local.length - 1, 1), 5))}${email.slice(at)}`;
}
